/**
 * Switch — toggle switch with label support.
 * Refined track and thumb with smooth, subtle transition.
 */

import { forwardRef } from 'react';
import { cn } from '@/lib/cn';

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  description?: string;
  disabled?: boolean;
  name?: string;
  className?: string;
  wrapperClassName?: string;
}

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  ({ checked, onChange, label, description, disabled, name, className, wrapperClassName }, ref) => {
    return (
      <div className={cn('flex items-start gap-2.5', wrapperClassName)}>
        <button
          ref={ref}
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={label}
          disabled={disabled}
          name={name}
          onClick={() => onChange(!checked)}
          className={cn(
            'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full',
            'transition-colors duration-fast ease-standard',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/15 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            'disabled:cursor-not-allowed disabled:opacity-40',
            checked ? 'bg-primary-700' : 'bg-neutral-200',
            className,
          )}
        >
          <span
            className={cn(
              'pointer-events-none absolute top-0.5 left-0.5 inline-block h-4 w-4 rounded-full bg-white',
              'shadow-xs transition-transform duration-fast ease-standard',
              checked ? 'translate-x-4' : 'translate-x-0',
            )}
          />
        </button>
        {label && (
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium text-foreground tracking-tight">{label}</span>
            {description && <p className="text-xs text-muted-foreground">{description}</p>}
          </div>
        )}
      </div>
    );
  },
);

Switch.displayName = 'Switch';
